import { useAddons } from '../cms/addons';
import { openAskAgent } from '../cms/askAgentBus';
import { track } from '../../lib/usage';
import { heldWords } from './approvalWords';
import React, {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { useProduct } from '../../context/ProductContext';
import { showInkToast } from '../cms/InkToast';
import { useFreshPost } from '../reach/FreshPost';
import { useCopy } from '../../lib/copyToClipboard';
import { ACCESS_SPANS } from '../settings/supportAccess';
import { ApprovalItem, useApprovalQueue } from './approvalQueue';
import { useLiveChanges } from '../cms/useLiveChanges';
import { sentToAgent } from './approvalWords';
import { appendChip, askChips, orderQueue, queueHeads } from './approvalOrder';
import type { NewSiteRow } from './newSiteWords';
import { ApprovalPreview } from './ApprovalPreview';
import { AGENT_ACTION_APPROVAL } from './approvalsGql';
import { REACH_POST_FOR_APPROVAL } from '../reach/reach';
import { VOICE_PATH } from '../reach/voiceWords';
import { ApprovalPost, approvalOutcome, MAX_NOTE } from '../reach/approvalOutcome';

/**
 * The Approvals review (design 2026-10-27, App Prototype "Approvals review";
 * backlog 301b9729): approvals moved off Today into a workspace that opens
 * over whatever screen the person is on, under the top bar (which stays
 * usable). They work the queue one item at a time — the real rendering of
 * what's proposed on the right, Approve / Ask for changes / Later pinned on
 * the left — and Esc or Close puts them back where they were. Opened from the
 * top bar's "N · To approve", the Today tab's count, Today's header, and an
 * agent's approval link (/approvals/:kind/:id, ApprovalsLink), which lands on
 * that item.
 */

export interface ApprovalTarget {
  kind: string;
  id: string;
}

/** The queue position a fix left from (router state on the editor it opened). */
interface ApprovalsBack extends ApprovalTarget {
  pos: number;
  total: number;
}

interface ApprovalsContextValue {
  /** Open on the first item, or on the one a link names. */
  open: (target?: ApprovalTarget) => void;
  close: () => void;
  isOpen: boolean;
  /** What waits on the person: the top bar and the Today tab show it. */
  count: number;
  /** Draft campaigns starting soon, not scheduled: listed, not counted. */
  soon: number;
  loading: boolean;
}

const ApprovalsContext = createContext<ApprovalsContextValue | null>(null);

/**
 * A site's "to approve" count: the queue's own for the site you're on, so it
 * can't disagree with the top bar (backlog 9e3c193a); the server's for the rest.
 * A Campaign-role person has no queue, so the server's stands for them.
 */
export function useSiteWaiting() {
  const { count, loading } = useApprovals();
  const { selectedAccount } = useAccount();
  return (site: { accountId: string; waiting: number }) =>
    site.accountId === selectedAccount?.id && !loading && !selectedAccount?.campaignRole
      ? count
      : site.waiting;
}

export function useApprovals(): ApprovalsContextValue {
  return (
    useContext(ApprovalsContext) || {
      open: () => undefined,
      close: () => undefined,
      isOpen: false,
      count: 0,
      soon: 0,
      loading: false,
    }
  );
}

const shortWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * What became of a linked item that no longer waits, in one line: a post's
 * outcome, or who answered an agent's request and when. Null when there's
 * nothing more specific to say than the general line.
 */
async function linkOutcome(
  apollo: ApolloClient<object>,
  target: ApprovalTarget
): Promise<string | null> {
  try {
    if (target.kind === 'post') {
      const { data } = await apollo.query<{ socialPost: ApprovalPost | null }>({
        query: REACH_POST_FOR_APPROVAL,
        variables: { id: target.id },
        fetchPolicy: 'network-only',
      });
      const said = data?.socialPost ? approvalOutcome(data.socialPost) : null;
      return said ? `${said.title}. ${said.text}` : null;
    }
    if (target.kind === 'agent') {
      const { data } = await apollo.query<{
        agentActionApproval: {
          status: string;
          decidedBy: string | null;
          decidedAt: string | null;
          expiresAt: string;
        } | null;
      }>({
        query: AGENT_ACTION_APPROVAL,
        variables: { id: target.id },
        fetchPolicy: 'network-only',
      });
      const r = data?.agentActionApproval;
      if (!r) return 'That request isn’t on this site, or it has gone.';
      const by = `${r.decidedBy || 'Someone'}${r.decidedAt ? `, ${shortWhen(r.decidedAt)}` : ''}`;
      if (r.status === 'approved') return `That request was already approved (${by}).`;
      if (r.status === 'declined') return `That request was declined (${by}).`;
      return 'That request lapsed: nobody answered within a day. Ask your agent to ask again.';
    }
  } catch {
    /* the general line stands */
  }
  return null;
}

/** Side by side from 900px; below it, the phone card (one item fills the screen). */
const PHONE_BELOW = 900;
const SWIPE_PX = 60;

/**
 * Around the signed-in shell (App.tsx AuthedShell): one queue for the top bar,
 * the Today tab, Today's header and approval links. tinycms only, and never
 * for the Campaign role (they see only their campaigns).
 */
export function ApprovalsProvider({ children }: { children: React.ReactNode }) {
  const { accountId, selectedAccount } = useAccount();
  const { product } = useProduct();
  const enabled = product === 'atomizer' && !selectedAccount?.campaignRole;
  const queue = useApprovalQueue({ skip: !enabled });
  const [session, setSession] = useState<{ target?: ApprovalTarget; site: string | null } | null>(
    null
  );

  const open = useCallback(
    (target?: ApprovalTarget) => setSession({ target, site: accountId || null }),
    [accountId]
  );
  const close = useCallback(() => setSession(null), []);

  // Left for the editor from a fix: back to that item, its place in the queue said.
  const location = useLocation();
  const navigate = useNavigate();
  const back = (location.state as { approvalsBack?: ApprovalsBack } | null)?.approvalsBack;
  const goBack = () => {
    if (!back) return;
    // Used once: the page's own history entry no longer offers it.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    open({ kind: back.kind, id: back.id });
  };

  // Switching site closes it; the top bar then shows that site's count.
  useEffect(() => {
    if (session && session.site && accountId && session.site !== accountId) setSession(null);
  }, [accountId, session]);

  const value = useMemo(
    () => ({
      open,
      close,
      isOpen: Boolean(session),
      count: enabled ? queue.count : 0,
      soon: enabled ? queue.items.length - queue.count : 0,
      loading: queue.loading,
    }),
    [open, close, session, enabled, queue.count, queue.items.length, queue.loading]
  );

  return (
    <ApprovalsContext.Provider value={value}>
      {children}
      {back && enabled && !session ? (
        <button type="button" className="ar-back" onClick={goBack}>
          ← Back to approvals ({back.pos} of {back.total})
        </button>
      ) : null}
      {session && enabled ? (
        <ApprovalsWorkspace
          // A new link opens fresh: its own item, no deferrals carried over.
          key={session.target ? `${session.target.kind}:${session.target.id}` : 'queue'}
          target={session.target}
          queue={queue}
          onClose={close}
        />
      ) : null}
    </ApprovalsContext.Provider>
  );
}

/** Where the top bar ends, so the workspace sits right under it at every width. */
function useHeaderBottom() {
  const [top, setTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    const header = document.querySelector('.ink-header');
    if (!header) return;
    const measure = () => setTop(Math.round(header.getBoundingClientRect().bottom));
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(header);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  return top;
}

function usePhone() {
  const read = () => (typeof window !== 'undefined' ? window.innerWidth < PHONE_BELOW : false);
  const [phone, setPhone] = useState(read);
  useEffect(() => {
    const onResize = () => setPhone(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return phone;
}

type Queue = ReturnType<typeof useApprovalQueue>;

function ApprovalsWorkspace({
  target,
  queue,
  onClose,
}: {
  target?: ApprovalTarget;
  queue: Queue;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const panelRef = useRef<HTMLDivElement>(null);
  const top = useHeaderBottom();
  const phone = usePhone();
  const { copy, state: copyState } = useCopy();
  const fresh = useFreshPost(() => void queue.refetch());

  // Keys taken off while their action settles (the queue refetches after).
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  // Deferred with Later: they go to the end of the list, in a LATER group.
  const [later, setLater] = useState<string[]>([]);
  const [currentKey, setCurrentKey] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [reason, setReason] = useState<string | null>(null);
  // The main image's alt text, typed in the queue (feedback 25c2adc6).
  const [alt, setAlt] = useState<string | null>(null);
  const [prompting, setPrompting] = useState(false);
  const [days, setDays] = useState(7);
  const [acting, setBusy] = useState(false);
  // An agent's edit landing while the person reviews: the queue refetches, and
  // the actions wait a moment while the preview catches up (useLiveChanges).
  const live = useLiveChanges(true, () => queue.refetch());
  const busy = acting || live.justChanged;
  const [moving, setMoving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Phone: Details open on the card, the queue sheet, the slide as items change.
  const [details, setDetails] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [shift, setShift] = useState(0);
  const resolvedTarget = useRef(false);

  // By group, oldest first within each, Later last (design review round 27, §6).
  const order = useMemo(
    () =>
      orderQueue(
        queue.items.filter((i) => !gone.has(i.key)),
        later
      ),
    [queue.items, gone, later]
  );
  const heads = useMemo(() => queueHeads(order, later), [order, later]);

  // The link's item, once the queue has loaded; said plainly if it isn't waiting.
  useEffect(() => {
    if (!target || resolvedTarget.current || queue.loading) return;
    resolvedTarget.current = true;
    const wanted = `${target.kind}:${target.id}`;
    const hit = queue.items.find((i) => i.match.includes(wanted));
    if (hit) {
      setCurrentKey(hit.key);
      return;
    }
    setNotice(
      'That one isn’t waiting for your approval any more. It may be live already, or sent back to your agent.'
    );
    void linkOutcome(apollo, target).then((said) => said && setNotice(said));
  }, [target, queue.items, queue.loading, apollo]);

  const index = Math.max(
    0,
    order.findIndex((i) => i.key === currentKey)
  );
  const item: ApprovalItem | null = order[index] || null;
  // Hold on to what's on screen by its key: a request arriving while the
  // person reads (the count refreshes every minute) joins the list without
  // taking over the decision pane. A link's item is set above instead.
  const shownKey = item?.key || null;
  useEffect(() => {
    if (queue.loading || !shownKey || currentKey) return;
    if (!target || resolvedTarget.current) setCurrentKey(shownKey);
  }, [shownKey, currentKey, target, queue.loading, notice]);

  const reset = () => {
    setAsking(false);
    setNote('');
    setReason(null);
    setAlt(null);
    setPrompting(false);
    setDays(7);
    setDetails(false);
  };
  const select = (key: string | null) => {
    reset();
    setCurrentKey(key);
  };
  // The next item slides in from the side it came from (phone).
  const slide = (d: number) => {
    if (!phone) return;
    setShift(d > 0 ? -40 : 40);
    window.setTimeout(() => setShift(0), 20);
  };
  const step = (d: number) => {
    if (order.length < 2) return;
    const next = order[(index + d + order.length) % order.length];
    setMoving(true);
    window.setTimeout(() => setMoving(false), 180);
    slide(d);
    select(next.key);
  };
  const deferCurrent = () => {
    if (!item) return;
    slide(1);
    const nextKey = order.length > 1 ? order[(index + 1) % order.length].key : item.key;
    setLater((l) => [...l.filter((k) => k !== item.key), item.key]);
    select(nextKey);
  };

  // Focus the panel so the keys work; give focus back on close.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    window.setTimeout(() => panelRef.current?.focus(), 30);
    return () => before?.focus?.();
  }, []);

  /** Run an action on the current item: it leaves the queue and the next becomes current. */
  const act = async (fn: () => Promise<void>, { leaves = true } = {}) => {
    if (!item || busy) return;
    const key = item.key;
    const nextKey = order.length > 1 ? order[(index + 1) % order.length].key : null;
    setBusy(true);
    try {
      await fn();
      if (leaves) {
        setGone((g) => new Set(g).add(key));
        select(nextKey);
      }
      await queue.refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
      // A refusal can leave news behind (a copy edit that ran and found
      // something): the card shows it, with Approve anyway.
      queue.refetch().catch(() => {});
    } finally {
      setGone((g) => {
        if (!g.has(key)) return g;
        const n = new Set(g);
        n.delete(key);
        return n;
      });
      setBusy(false);
    }
  };

  const sendAsk = () =>
    act(async () => {
      if (!item?.ask) return;
      const text = note.trim();
      if (!text) return;
      const undo = await item.ask(text);
      showInkToast(sentToAgent(text, item.key.startsWith('agent:') ? 'request' : 'version'), {
        label: 'Undo',
        onClick: () =>
          void undo()
            .then(() => queue.refetch())
            .catch((err) =>
              showInkToast(err instanceof Error ? err.message : 'Couldn’t undo that.')
            ),
      });
    });

  // Where a fix leaves for the editor, it takes the queue position along: the
  // page it lands on offers "Back to approvals (1 of 34)" (feedback 25c2adc6).
  const leaveTo = (to: string) => {
    const [kind, id] = (item?.match[0] || '').split(':');
    onClose();
    navigate(to, {
      state:
        kind && id ? { approvalsBack: { kind, id, pos: index + 1, total: order.length } } : null,
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    // Typing a note, a reason or alt text is never hijacked.
    if (asking || reason !== null || alt !== null || tag === 'TEXTAREA' || tag === 'INPUT') return;
    if (e.key === 'Escape' && queueOpen) {
      e.preventDefault();
      setQueueOpen(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      deferCurrent();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      step(-1);
    }
  };

  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const from = touchX.current;
    touchX.current = null;
    if (from == null || asking || reason !== null || queueOpen) return;
    const dx = (e.changedTouches[0]?.clientX ?? from) - from;
    if (Math.abs(dx) > SWIPE_PX) {
      if (dx < 0) deferCurrent();
      else step(-1);
    }
  };

  const pos = order.length ? `${index + 1} of ${order.length}` : '';
  const style = top != null ? ({ '--ar-top': `${top}px` } as React.CSSProperties) : undefined;

  // ── Pieces both layouts use ──────────────────────────────────────────────
  const history = () => leaveTo('/settings/activity?filter=approval');
  const closeAsk = () => {
    setAsking(false);
    setNote('');
  };
  const queueRows = (pick: (key: string) => void) =>
    order.map((q, k) => (
      <Fragment key={q.key}>
        {heads[k] ? <div className="ar-group-head">{heads[k]}</div> : null}
        <button
          type="button"
          className="ar-row"
          aria-current={q.key === item?.key ? 'true' : undefined}
          onClick={() => pick(q.key)}
        >
          <span className="ar-tag" data-tone={q.failed ? 'failed' : undefined}>
            {q.tag}
          </span>
          <span className="ar-row-title">{q.title}</span>
          {later.includes(q.key) ? <span className="ar-row-later">Later</span> : null}
          <span className="ar-row-age">{q.ago}</span>
        </button>
      </Fragment>
    ));
  const meta = item ? (
    <div className="ar-card-meta">
      <span className="ar-tag" data-tone={item.failed ? 'failed' : undefined}>
        {item.tag}
      </span>
      <span className="ar-who">{item.who}</span>
      <span className="ar-age">{item.ago}</span>
    </div>
  ) : null;
  // A new site from a template (design 2026-10-27): what went in for the person, and what's left.
  const newSite = item?.newSite ? (
    <NewSiteLists site={item.newSite} onOpen={(to) => leaveTo(to)} />
  ) : null;
  const checks = (hint = false) =>
    item ? (
      <div className="ar-checks">
        <div className="ar-checks-head">CHECKS</div>
        <div className="ar-check" data-tone={item.check.tone}>
          {item.check.text}
        </div>
        {item.voice ? (
          <div className="ar-voice">
            {item.voice.rows.map((row) => (
              <div className="vs-check" key={row.text}>
                <span style={{ color: row.color, fontWeight: 600 }}>{row.icon}</span>
                <span>{row.text}</span>
                <span className="vs-check-sev" style={{ color: row.color }}>
                  {row.sev}
                </span>
              </div>
            ))}
            <div className="ar-voice-links">
              <button type="button" className="ar-link" onClick={() => void item.voice?.rewrite()}>
                Rewrite in your voice
              </button>
              <button type="button" className="ar-link" onClick={() => leaveTo(VOICE_PATH)}>
                Voice &amp; style settings
              </button>
            </div>
          </div>
        ) : null}
        <div className="ar-effect">{item.effect}</div>
        {hint ? <div className="ar-hint">Swipe left for later · right to go back</div> : null}
      </div>
    ) : null;
  const hasNote = Boolean(note.trim());
  const chips = item?.ask ? askChips(item) : [];
  const askFields = (
    <>
      <textarea
        ref={noteRef}
        className="ar-textarea"
        autoFocus
        rows={4}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What should change?"
        aria-label="What should change"
        maxLength={MAX_NOTE}
      />
      {chips.length ? (
        <div className="ar-chips">
          {chips.map((label) => (
            <button
              type="button"
              key={label}
              className="ar-chip"
              onClick={() => {
                setNote((n) => appendChip(n, label, MAX_NOTE));
                noteRef.current?.focus();
              }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      <div className="ar-help">
        This does not approve anything. It goes back to your agent as a request and returns here as
        a new version.
      </div>
      <div className="ar-btn-row">
        <button type="button" className="ar-btn" onClick={closeAsk} disabled={busy}>
          Back
        </button>
        {/* The note is required (round 27 §5): grey until it has text. */}
        <button
          type="button"
          className="ar-btn ar-btn-primary"
          onClick={() => (hasNote ? void sendAsk() : noteRef.current?.focus())}
          aria-disabled={!hasNote || undefined}
          disabled={busy}
        >
          Send request
        </button>
      </div>
    </>
  );
  // A blocking check goes live only with a reason, recorded with the approval.
  const reasonForm = (className: string) =>
    item && reason !== null ? (
      <form
        className={className}
        onSubmit={(e) => {
          e.preventDefault();
          const why = reason.trim();
          if (why && item.approveAnyway) {
            if (item.heldForOwners) track('held_override', { label: 'admin' });
            void act(() => item.approveAnyway!(why));
          }
        }}
      >
        <input
          className="ar-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why it can go live as it is"
          aria-label="Reason to approve anyway"
          autoFocus
        />
        <div className="ar-help">Recorded with your approval.</div>
        <div className="ar-btn-row">
          <button type="button" className="ar-btn" onClick={() => setReason(null)} disabled={busy}>
            Back
          </button>
          <button type="submit" className="ar-btn ar-btn-primary" disabled={busy || !reason.trim()}>
            {item.cta} anyway
          </button>
        </div>
      </form>
    ) : null;
  // The main image's alt text, saved to the article; the item stays and its
  // checks move on to what's left.
  const altForm = (className: string) =>
    item?.heroAlt && alt !== null ? (
      <form
        className={className}
        onSubmit={(e) => {
          e.preventDefault();
          const text = alt.trim();
          if (text) void act(() => item.heroAlt!(text), { leaves: false }).then(() => setAlt(null));
        }}
      >
        <input
          className="ar-reason"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder="Describe the main image for someone who can’t see it"
          aria-label="Alt text for the main image"
          maxLength={300}
          autoFocus
        />
        <div className="ar-help">Saved to the article. Readers with screen readers hear it.</div>
        <div className="ar-btn-row">
          <button type="button" className="ar-btn" onClick={() => setAlt(null)} disabled={busy}>
            Back
          </button>
          <button type="submit" className="ar-btn ar-btn-primary" disabled={busy || !alt.trim()}>
            Save alt text
          </button>
        </div>
      </form>
    ) : null;
  const spans = item?.approveFor ? (
    <div className="ar-spans" role="group" aria-label="How long">
      {ACCESS_SPANS.map((s) => (
        <button
          type="button"
          key={s.days}
          aria-pressed={days === s.days}
          onClick={() => setDays(s.days)}
        >
          {s.label}
        </button>
      ))}
    </div>
  ) : null;
  const promptBox =
    prompting && item?.prompt ? <div className="ar-prompt">{item.prompt}</div> : null;
  const { isOwner } = useAddons();
  const primary = (className: string) =>
    item ? (
      <Primary
        className={className}
        item={item}
        busy={busy}
        days={days}
        prompting={prompting}
        copied={copyState('prompt')}
        onApprove={() => void act(item.approve)}
        onApproveFor={() => void act(() => item.approveFor!(days))}
        onFix={() => {
          if (item.heroAlt) {
            setAlt('');
            setDetails(true);
          } else if (item.held) {
            openAskAgent({
              action: 'healthFix',
              ctx: {
                title: item.title,
                fix:
                  item.heldFix ||
                  `Fix what’s held on “${item.title}”: ${heldWords(item.check?.text)}`,
              },
            });
          } else if (item.fix) leaveTo(item.fix.to);
        }}
        onPrompt={(e) => (prompting ? copy('prompt', item.prompt || '', e) : setPrompting(true))}
      />
    ) : null;
  // "Approve anyway" beside an open fix, Skip copy edit beside an approval that
  // would run it, Skip for a passed post, a fresh post for old news.
  const moreActions = item ? (
    <>
      {item.fix && item.fixSecondary ? (
        // The approval leads; the open warning's fix waits beside it (a new site's share image).
        <button type="button" className="ar-btn" onClick={() => leaveTo(item.fix!.to)}>
          {item.fix.label}
        </button>
      ) : item.fix && item.heldForOwners && !isOwner ? (
        <span className="ink-held-note">Only owners and admins can approve held items.</span>
      ) : item.fix ? (
        <button
          type="button"
          className="ar-btn"
          disabled={busy}
          onClick={() =>
            (item.blocked || item.copyEditPending) && item.approveAnyway
              ? setReason('')
              : void act(item.approve)
          }
        >
          {item.heldForOwners ? `${item.cta} anyway, with a reason` : `${item.cta} anyway`}
        </button>
      ) : null}
      {item.copyEditPending && item.approveAnyway && (!item.fix || item.fixSecondary) ? (
        // Going live without it is an override, so it asks why, as Approve anyway does.
        <button type="button" className="ar-btn" disabled={busy} onClick={() => setReason('')}>
          Skip copy edit
        </button>
      ) : null}
      {item.extra ? (
        <button
          type="button"
          className="ar-btn"
          disabled={busy}
          onClick={() => void act(item.extra!.run)}
        >
          {item.extra.label}
        </button>
      ) : null}
      {item.fresh ? (
        <button
          type="button"
          className="ar-btn"
          disabled={busy}
          onClick={() => void fresh.open(item.fresh!)}
        >
          Write a fresh post
        </button>
      ) : null}
    </>
  ) : null;
  // Ask for changes, or what stands in for it (Skip, Dismiss, Decline).
  const second = (className: string) =>
    item?.ask ? (
      <button type="button" className={className} disabled={busy} onClick={() => setAsking(true)}>
        Ask for changes
      </button>
    ) : item?.secondary ? (
      <button
        type="button"
        className={className}
        disabled={busy}
        onClick={() => void act(item.secondary!.run)}
      >
        {item.secondary.label}
      </button>
    ) : null;

  return (
    <div
      ref={panelRef}
      className="ar"
      data-screen-label="Approvals review"
      data-phone={phone ? 'true' : 'false'}
      role="region"
      aria-label="Approvals"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      style={style}
    >
      <div className="ar-head">
        <span className="ar-head-title">Approvals</span>
        {pos && phone ? (
          // Phone: the position opens the queue sheet.
          <button
            type="button"
            className="ar-pos ar-pos-btn"
            onClick={() => setQueueOpen(true)}
            aria-label={`${pos}: show the queue`}
          >
            {pos}
            <span aria-hidden="true">▾</span>
          </button>
        ) : pos ? (
          <span className="ar-pos">{pos}</span>
        ) : null}
        <span className="ar-head-tools">
          <button type="button" className="ar-history" onClick={history}>
            History
          </button>
          <button type="button" className="ar-close" onClick={onClose} title="Close (Esc)">
            Close<span className="ar-kbd">Esc</span>
          </button>
        </span>
      </div>
      {notice ? (
        <div className="ar-notice" role="status">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">
            Dismiss
          </button>
        </div>
      ) : null}

      {!item ? (
        queue.loading ? (
          <div className="ar-empty">
            <div className="ar-empty-text">Loading what’s to approve…</div>
          </div>
        ) : (
          <div className="ar-empty">
            <div className="ar-empty-inner">
              <span className="ar-empty-tick" aria-hidden="true">
                ✓
              </span>
              <div className="ar-empty-title">All caught up</div>
              <div className="ar-empty-text">
                Your agent asks here before anything goes live. New requests show as a count in the
                top bar.
              </div>
              <div className="ar-empty-btns">
                <button type="button" className="ar-btn" onClick={history}>
                  Approval history
                </button>
                <button type="button" className="ar-btn ar-btn-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )
      ) : phone ? (
        // Phone (design 2026-10-27, "Approvals on Phone"): one item fills the
        // screen as a card — the rendering gets the room, the note and checks
        // wait behind Details, and the actions float in a pill.
        <>
          <div className="ar-progress" aria-hidden="true">
            <i style={{ width: `${((index + 1) / order.length) * 100}%` }} />
          </div>
          <div
            className="ar-mcard"
            data-moving={moving ? 'true' : undefined}
            style={shift ? { transform: `translateX(${shift}px)` } : undefined}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="ar-mhead">
              {meta}
              <h2 className="ar-title">{item.title}</h2>
              {spans}
              {promptBox}
              <div className="ar-mcheck">
                <span className="ar-check" data-tone={item.check.tone}>
                  {item.check.text}
                </span>
                <button
                  type="button"
                  className="ar-link"
                  aria-expanded={details}
                  onClick={() => setDetails((d) => !d)}
                >
                  {details ? 'Less' : 'Details'}
                </button>
                <button type="button" className="ar-link" onClick={() => leaveTo(item.editorTo)}>
                  Open in editor →
                </button>
              </div>
              {details ? (
                <>
                  {item.note ? <div className="ar-note">{item.note}</div> : null}
                  {newSite}
                  {checks(true)}
                  {reason !== null ? (
                    reasonForm('ar-mmore')
                  ) : alt !== null ? (
                    altForm('ar-mmore')
                  ) : (
                    <div className="ar-mmore">{moreActions}</div>
                  )}
                </>
              ) : null}
            </div>
            <ApprovalPreview key={item.key} item={item} siteName={queue.siteName} />
          </div>
          <div className="ar-pill" data-screen-label="Approvals · floating actions">
            {live.justChanged || live.agentWorking ? (
              <span
                role="status"
                style={{
                  font: '500 12px var(--ink-font)',
                  color: 'var(--ink-ink2)',
                  padding: '0 6px',
                  whiteSpace: 'nowrap',
                }}
              >
                {live.justChanged ? 'Updated just now' : 'Your agent is updating…'}
              </span>
            ) : null}
            <button type="button" className="ar-pill-later" disabled={busy} onClick={deferCurrent}>
              Later
            </button>
            {second('ar-pill-mid')}
            {primary('ar-pill-primary')}
          </div>
          {asking ? (
            <div className="ar-scrim" onClick={closeAsk}>
              <div
                className="ar-sheet"
                role="dialog"
                aria-label="Ask for changes"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ar-sheet-title">Ask for changes</div>
                {askFields}
              </div>
            </div>
          ) : null}
          {queueOpen ? (
            <div className="ar-scrim" onClick={() => setQueueOpen(false)}>
              <div
                className="ar-sheet ar-sheet-queue"
                role="dialog"
                aria-label="To approve"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ar-queue-head">
                  <span>TO APPROVE</span>
                  <span>{pos}</span>
                </div>
                {queueRows((key) => {
                  select(key);
                  setQueueOpen(false);
                })}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="ar-body">
          <div className="ar-decide" data-screen-label="Approvals · decision">
            <div className="ar-queue">
              <div className="ar-queue-head">
                <span>TO APPROVE</span>
                <span>{pos}</span>
              </div>
              {queueRows(select)}
            </div>

            <div
              className="ar-card"
              data-moving={moving ? 'true' : undefined}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              {meta}
              <h2 className="ar-title">{item.title}</h2>
              {item.note ? <div className="ar-note">{item.note}</div> : null}
              {newSite}
              {checks()}
            </div>

            {/* Pinned to the pane, not the card: when the pane is short the
                queue and the card scroll under the actions. */}
            {asking ? (
              <div className="ar-actions">{askFields}</div>
            ) : reason !== null ? (
              reasonForm('ar-actions')
            ) : alt !== null ? (
              altForm('ar-actions')
            ) : (
              <div className="ar-actions">
                {spans}
                {promptBox}
                {primary('ar-btn ar-btn-primary')}
                {moreActions}
                <div className="ar-btn-row">
                  {second('ar-btn')}
                  <button
                    type="button"
                    className="ar-btn ar-btn-quiet"
                    disabled={busy}
                    onClick={deferCurrent}
                  >
                    Later
                  </button>
                </div>
                <div className="ar-foot">
                  <span>← → to move through the queue</span>
                  <button type="button" onClick={() => leaveTo(item.editorTo)}>
                    Open in editor →
                  </button>
                </div>
              </div>
            )}
          </div>
          <ApprovalPreview key={item.key} item={item} siteName={queue.siteName} />
        </div>
      )}
      {fresh.node}
    </div>
  );
}

/**
 * FILLED IN FOR YOU and CHANGE WHENEVER YOU LIKE (design §3.3–3.4): each
 * filled-in value with where it went and Change; each placeholder group with
 * where it is and Edit, which opens that page at its first block.
 */
function NewSiteLists({
  site,
  onOpen,
}: {
  site: NonNullable<ApprovalItem['newSite']>;
  onOpen: (to: string) => void;
}) {
  const row = (r: NewSiteRow, i: number, action: string) => (
    <div key={`${i}:${r.value}`} className="ar-ns-row">
      <span className="ar-ns-dot" aria-hidden="true" />
      <div className="ar-ns-text">
        <div className="ar-ns-value" title={r.value}>
          {r.value}
        </div>
        <div className="ar-ns-where">{r.where}</div>
      </div>
      {r.to ? (
        <button type="button" className="ar-ns-act" onClick={() => onOpen(r.to as string)}>
          {action}
        </button>
      ) : (
        <span />
      )}
    </div>
  );
  return (
    <>
      <div className="ar-ns" data-screen-label="Approvals · Filled in for you">
        <div className="ar-ns-head">
          <span>FILLED IN FOR YOU</span>
          <span className="ar-ns-count">{site.filledCount}</span>
        </div>
        <div className="ar-ns-list">{site.filled.map((r, i) => row(r, i, 'Change'))}</div>
      </div>
      {site.placeholders.length ? (
        <div
          className="ar-ns"
          data-kind="placeholders"
          data-screen-label="Approvals · Change whenever you like"
        >
          <div className="ar-ns-head">
            <span>CHANGE WHENEVER YOU LIKE</span>
            <span className="ar-ns-count">{site.placeholderCount}</span>
          </div>
          <div className="ar-ns-list">{site.placeholders.map((r, i) => row(r, i, 'Edit'))}</div>
          <div className="ar-ns-note">{site.placeholderNote}</div>
        </div>
      ) : null}
    </>
  );
}

/** The one primary action: the item's approve, its fix, a span for access, or the agent prompt. */
function Primary({
  className,
  item,
  busy,
  days,
  prompting,
  copied,
  onApprove,
  onApproveFor,
  onFix,
  onPrompt,
}: {
  className: string;
  item: ApprovalItem;
  busy: boolean;
  days: number;
  prompting: boolean;
  copied: string | null | undefined;
  onApprove: () => void;
  onApproveFor: () => void;
  onFix: () => void;
  onPrompt: (e: React.MouseEvent) => void;
}) {
  if (item.fix && !item.fixSecondary) {
    return (
      <button type="button" className={className} onClick={onFix}>
        {item.fix.label}
      </button>
    );
  }
  if (item.prompt) {
    return (
      <button type="button" className={className} onClick={onPrompt}>
        {!prompting
          ? item.cta
          : copied === 'copied'
            ? 'Copied'
            : copied === 'failed'
              ? 'Couldn’t copy'
              : 'Copy prompt for your agent'}
      </button>
    );
  }
  if (item.approveFor) {
    const span = ACCESS_SPANS.find((s) => s.days === days)?.label || `${days} days`;
    return (
      <button type="button" className={className} disabled={busy} onClick={onApproveFor}>
        Approve for {span}
      </button>
    );
  }
  return (
    <button type="button" className={className} disabled={busy} onClick={onApprove}>
      {busy ? 'Working…' : item.cta}
    </button>
  );
}

/** The top bar's "N · To approve" (≥ 1100px), first in the tools. */
export function ApprovalsTopButton() {
  const { count, open } = useApprovals();
  if (!count) return null;
  return (
    <button
      type="button"
      className="ar-topbtn"
      data-approve-btn=""
      title="Awaiting your approval"
      aria-label={`${count} awaiting your approval`}
      onClick={() => open()}
    >
      <span className="ar-count">{count}</span>
      <span className="ar-topbtn-label">To approve</span>
    </button>
  );
}

/** Today's header. The number is the top bar's alone (backlog 672ace6e). */
export function ApprovalsReviewButton() {
  const { open } = useApprovals();
  return (
    <button type="button" className="ar-review-btn" onClick={() => open()}>
      <i aria-hidden="true" />
      Start reviewing →
    </button>
  );
}

/** "kind:id" from a link the workspace can open (/approvals/:kind/:id). */
export const APPROVAL_KINDS = [
  'article',
  'page',
  'form',
  'site',
  'dataset',
  'post',
  'campaign',
  'agent',
  'support',
];
